import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
} from "@tiptap/react";
import { ActionIcon, Group, Menu, Tooltip } from "@mantine/core";
import {
  IconArrowRight,
  IconArrowsLeftRight,
  IconMinus,
  IconPlus,
} from "@tabler/icons-react";
import { Fragment } from "@tiptap/pm/model";
import classes from "./layout-blocks.module.css";
import { EditTrigger } from "./edit-trigger";
import {
  openConfigureChips,
  type ChipConfig,
} from "./configure-chips-modal";
import {
  openConfigureFeatures,
  type FeatureConfig,
} from "./configure-features-modal";
import { openSingleIconText } from "./single-icon-text-modal";
import { openTextPrompt } from "./text-prompt-modal";

// Tiptap PMNode text helpers ----------------------------------------------

function inlineTextOf(node: any): string {
  let out = "";
  node.descendants?.((n: any) => {
    if (n.isText) out += n.text ?? "";
  });
  return out;
}

function pageIdOf(editor: any): string | undefined {
  return editor?.storage?.pageId as string | undefined;
}

// Composes the layoutWrapper class plus an optional variant modifier so a
// single per-node container drives both edit-trigger visibility and the
// `--v-*` CSS custom properties consumed by .chipDisc / .featureItem etc.
function wrapperClass(variant?: string) {
  const v = variant ?? "default";
  const variantClass = (classes as Record<string, string>)[`v-${v}`] ?? classes["v-default"];
  return `${classes.layoutWrapper} ${variantClass}`;
}

// ──────────────────────────────────────────────────────────────────────
// 1. IconChipGridView — outer grid container.
// ──────────────────────────────────────────────────────────────────────
export function IconChipGridView(props: NodeViewProps) {
  const { node, editor, getPos, selected } = props;
  const onEdit = async () => {
    const initial: ChipConfig[] = [];
    node.forEach((child) => {
      if (child.type.name !== "iconChip") return;
      initial.push({
        iconSrc: child.attrs.iconSrc ?? "",
        iconAlt: child.attrs.iconAlt ?? "",
        label: inlineTextOf(child),
        href: child.attrs.href ?? "",
        variant: child.attrs.variant ?? "default",
      });
    });
    const next = await openConfigureChips(initial, {
      pageId: pageIdOf(editor),
      mode: "edit",
    });
    if (!next || typeof getPos !== "function") return;
    const pos = getPos();
    if (pos == null) return;
    const newGrid = editor.schema.nodes.iconChipGrid.create(
      { columns: node.attrs.columns ?? 6 },
      Fragment.from(
        next.map((c) =>
          editor.schema.nodes.iconChip.create(
            {
              iconSrc: c.iconSrc || null,
              iconAlt: c.iconAlt ?? "",
              href: c.href?.trim() || null,
              variant: c.variant ?? "default",
            },
            c.label
              ? Fragment.from(editor.schema.text(c.label))
              : Fragment.empty,
          ),
        ),
      ),
    );
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.replaceWith(pos, pos + node.nodeSize, newGrid);
        return true;
      })
      .run();
  };

  return (
    <NodeViewWrapper
      className={`${classes.layoutWrapper} ${classes.chipGrid}`}
      data-type="iconChipGrid"
      data-columns={node.attrs.columns ?? 6}
      data-selected={selected ? "true" : undefined}
    >
      {/* Children (iconChip) render through their own NodeView, each
          providing the disc + label structure styled by the CSS module. */}
      <NodeViewContent as="div" style={{ display: "contents" }} />
      <EditTrigger onClick={onEdit} label="Edit chips" />
    </NodeViewWrapper>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 1b. IconChipView — disc + label per chip (variant tint comes from
// CSS custom props on the wrapper).
// ──────────────────────────────────────────────────────────────────────
export function IconChipView(props: NodeViewProps) {
  const { node } = props;
  const variant = node.attrs.variant ?? "default";
  const iconSrc = node.attrs.iconSrc as string | null;
  const iconAlt = (node.attrs.iconAlt as string) ?? "";
  return (
    <NodeViewWrapper
      as="div"
      className={`${wrapperClass(variant)} ${classes.chip}`}
      data-type="iconChip"
      data-variant={variant}
    >
      <span className={classes.chipDisc} contentEditable={false}>
        {iconSrc ? (
          <img src={iconSrc} alt={iconAlt} loading="lazy" />
        ) : (
          <span className={classes.iconPlaceholder} />
        )}
      </span>
      <NodeViewContent as={"span" as any} className={classes.chipLabel} />
    </NodeViewWrapper>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 2. FeatureListView + 2b. FeatureItemView
// ──────────────────────────────────────────────────────────────────────
export function FeatureListView(props: NodeViewProps) {
  const { node, editor, getPos, selected } = props;
  const onEdit = async () => {
    const initial: FeatureConfig[] = [];
    node.forEach((child) => {
      if (child.type.name !== "featureItem") return;
      initial.push({
        iconSrc: child.attrs.iconSrc ?? "",
        iconAlt: child.attrs.iconAlt ?? "",
        title: child.attrs.title ?? "",
        badge: child.attrs.badge ?? "",
        text: inlineTextOf(child),
        variant: child.attrs.variant ?? "default",
      });
    });
    const next = await openConfigureFeatures(initial, {
      pageId: pageIdOf(editor),
      mode: "edit",
    });
    if (!next || typeof getPos !== "function") return;
    const pos = getPos();
    if (pos == null) return;
    const newList = editor.schema.nodes.featureList.create(
      null,
      Fragment.from(
        next.map((it) =>
          editor.schema.nodes.featureItem.create(
            {
              iconSrc: it.iconSrc || null,
              iconAlt: it.iconAlt ?? "",
              title: it.title ?? "",
              badge: it.badge ?? "",
              variant: it.variant ?? "default",
            },
            Fragment.from(
              editor.schema.nodes.paragraph.create(
                null,
                it.text
                  ? Fragment.from(editor.schema.text(it.text))
                  : Fragment.empty,
              ),
            ),
          ),
        ),
      ),
    );
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.replaceWith(pos, pos + node.nodeSize, newList);
        return true;
      })
      .run();
  };

  return (
    <NodeViewWrapper
      className={`${classes.layoutWrapper} ${classes.featureList}`}
      data-type="featureList"
      data-selected={selected ? "true" : undefined}
    >
      <NodeViewContent as="div" style={{ display: "contents" }} />
      <EditTrigger onClick={onEdit} label="Edit features" />
    </NodeViewWrapper>
  );
}

export function FeatureItemView(props: NodeViewProps) {
  const { node } = props;
  const variant = node.attrs.variant ?? "default";
  const iconSrc = node.attrs.iconSrc as string | null;
  const iconAlt = (node.attrs.iconAlt as string) ?? "";
  const title = (node.attrs.title as string) ?? "";
  const badge = (node.attrs.badge as string) ?? "";
  return (
    <NodeViewWrapper
      as="div"
      className={`${wrapperClass(variant)} ${classes.featureItem}`}
      data-type="featureItem"
      data-variant={variant}
    >
      <span className={classes.featureIcon} contentEditable={false}>
        {iconSrc ? (
          <img src={iconSrc} alt={iconAlt} loading="lazy" />
        ) : (
          <span className={classes.iconPlaceholder} />
        )}
      </span>
      <div className={classes.featureBody}>
        {title ? (
          <div className={classes.featureTitle} contentEditable={false}>
            {title}
          </div>
        ) : null}
        <NodeViewContent as="div" className={classes.featureDesc} />
        {badge ? (
          <div className={classes.featureBadge} contentEditable={false}>
            {badge}
          </div>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 3. InlineHighlightView + 4. FooterCalloutView — single icon + inline text.
// ──────────────────────────────────────────────────────────────────────
function singleIconTextView(
  blockName: "inlineHighlight" | "footerCallout",
  title: string,
  variantWrapperCls: string,
  textCls: string,
  withDisc: boolean,
) {
  return function View(props: NodeViewProps) {
    const { node, editor, getPos, selected } = props;
    const variant = node.attrs.variant ?? "default";
    const iconSrc = node.attrs.iconSrc as string | null;
    const iconAlt = (node.attrs.iconAlt as string) ?? "";

    const onEdit = async () => {
      const next = await openSingleIconText(
        {
          iconSrc: node.attrs.iconSrc ?? "",
          iconAlt: node.attrs.iconAlt ?? "",
          text: inlineTextOf(node),
          variant: node.attrs.variant ?? "default",
        },
        { pageId: pageIdOf(editor), title, mode: "edit" },
      );
      if (!next || typeof getPos !== "function") return;
      const pos = getPos();
      if (pos == null) return;
      const newNode = editor.schema.nodes[blockName].create(
        {
          iconSrc: next.iconSrc || null,
          iconAlt: next.iconAlt ?? "",
          variant: next.variant ?? "default",
        },
        next.text ? Fragment.from(editor.schema.text(next.text)) : Fragment.empty,
      );
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.replaceWith(pos, pos + node.nodeSize, newNode);
          return true;
        })
        .run();
    };

    return (
      <NodeViewWrapper
        className={`${wrapperClass(variant)} ${variantWrapperCls}`}
        data-type={blockName}
        data-variant={variant}
        data-selected={selected ? "true" : undefined}
      >
        {withDisc ? (
          <span className={classes.inlineDisc} contentEditable={false}>
            {iconSrc ? (
              <img src={iconSrc} alt={iconAlt} loading="lazy" />
            ) : (
              <span className={classes.iconPlaceholder} />
            )}
          </span>
        ) : iconSrc ? (
          <img src={iconSrc} alt={iconAlt} loading="lazy" contentEditable={false} />
        ) : null}
        <NodeViewContent as="div" className={textCls} />
        <EditTrigger onClick={onEdit} label={`Edit ${title.toLowerCase()}`} />
      </NodeViewWrapper>
    );
  };
}

export const InlineHighlightView = singleIconTextView(
  "inlineHighlight",
  "Inline highlight",
  classes.inlineHighlight,
  classes.inlineText,
  true,
);
export const FooterCalloutView = singleIconTextView(
  "footerCallout",
  "Footer callout",
  classes.footerCallout,
  classes.footerText,
  false,
);

// ──────────────────────────────────────────────────────────────────────
// 5. TitledFrameView — single attr (title), inline editable via prompt.
//    CSS module renders title as `::before` via `data-title` attr so we
//    don't have to inject a separate DOM node that ProseMirror might
//    fight over.
// ──────────────────────────────────────────────────────────────────────
export function TitledFrameView(props: NodeViewProps) {
  const { node, updateAttributes, selected } = props;
  const title = (node.attrs.title as string) ?? "Section";
  const onEdit = async () => {
    const raw = await openTextPrompt({
      title: "Titled frame",
      label: "Frame title",
      initial: title,
      submitLabel: "Save",
    });
    if (raw === null) return;
    updateAttributes({ title: raw.trim() || "Section" });
  };
  return (
    <NodeViewWrapper
      className={`${classes.layoutWrapper} ${classes.titledFrame}`}
      data-type="titledFrame"
      data-title={title}
      data-selected={selected ? "true" : undefined}
    >
      <NodeViewContent as="div" />
      <EditTrigger onClick={onEdit} label="Edit title" />
    </NodeViewWrapper>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 6. SectionDividerView — full-width heading with strokes on both sides.
// ──────────────────────────────────────────────────────────────────────
export function SectionDividerView(_props: NodeViewProps) {
  return (
    <NodeViewWrapper
      className={`${classes.layoutWrapper} ${classes.sectionDivider}`}
      data-type="sectionDivider"
    >
      <NodeViewContent as={"span" as any} className={classes.sectionDividerText} />
    </NodeViewWrapper>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 7. MappingTableView — connector toggle + add/remove row buttons.
// ──────────────────────────────────────────────────────────────────────
export function MappingTableView(props: NodeViewProps) {
  const { node, editor, getPos, updateAttributes, selected } = props;
  const connector = (node.attrs.connector ?? "arrow") as
    | "arrow"
    | "double-arrow";

  const addRow = () => {
    if (typeof getPos !== "function") return;
    const pos = getPos();
    if (pos == null) return;
    const cellType = editor.schema.nodes.mappingCell;
    const rowType = editor.schema.nodes.mappingRow;
    const paraType = editor.schema.nodes.paragraph;
    if (!cellType || !rowType || !paraType) return;
    const row = rowType.create(
      null,
      Fragment.from([
        cellType.create(null, paraType.create()),
        cellType.create(null, paraType.create()),
      ]),
    );
    const insertAt = pos + node.nodeSize - 1;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.insert(insertAt, row);
        return true;
      })
      .run();
  };

  const removeRow = () => {
    if (typeof getPos !== "function" || node.childCount <= 1) return;
    const pos = getPos();
    if (pos == null) return;
    const lastRow = node.lastChild;
    if (!lastRow) return;
    const lastRowStart = pos + node.nodeSize - 1 - lastRow.nodeSize;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.delete(lastRowStart, lastRowStart + lastRow.nodeSize);
        return true;
      })
      .run();
  };

  const toggleConnector = () => {
    updateAttributes({
      connector: connector === "arrow" ? "double-arrow" : "arrow",
    });
  };

  return (
    <NodeViewWrapper
      className={`${classes.layoutWrapper} ${classes.mappingTable}`}
      data-type="mappingTable"
      data-connector={connector}
      data-selected={selected ? "true" : undefined}
    >
      <NodeViewContent as="div" style={{ display: "contents" }} />
      <div
        contentEditable={false}
        data-edit-trigger
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 2,
          pointerEvents: "auto",
        }}
      >
        <Group gap={4}>
          <Tooltip
            label={
              connector === "arrow"
                ? "Switch to ↔ double arrow"
                : "Switch to → arrow"
            }
            withArrow
          >
            <ActionIcon
              size="sm"
              variant="default"
              onClick={toggleConnector}
              aria-label="Toggle connector"
            >
              {connector === "arrow" ? (
                <IconArrowRight size={14} />
              ) : (
                <IconArrowsLeftRight size={14} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Add row" withArrow>
            <ActionIcon
              size="sm"
              variant="default"
              onClick={addRow}
              aria-label="Add row"
            >
              <IconPlus size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Remove last row" withArrow>
            <ActionIcon
              size="sm"
              variant="default"
              disabled={node.childCount <= 1}
              onClick={removeRow}
              aria-label="Remove row"
            >
              <IconMinus size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </div>
    </NodeViewWrapper>
  );
}

// Quiet unused-import warning (kept around for symmetry with other NodeViews).
void Menu;
